import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react'
import { Tooltip } from '@mui/material'
import { eventBus, EVENTS } from '../../../utils/EventBus'
import { ColumnManager } from '../../../utils/ColumnManager'
import { getKeyboardNavigationDirection } from './DistributionTable/utils/keyboardNavigation'

const STATUS_COLOR_MAP = {
  0: 'transparent',
  1: '#FFFF00',
  2: '#FFC0CB',
  3: '#808080',
  4: '#0000FF',
  5: '#ADD8E6',
  7: '#6CB4D9',
  '-10': 'transparent'
}

const STATUS_TEXT_COLOR_MAP = {
  0: '#000000',
  1: '#000000',
  2: '#000000',
  3: '#FFFFFF',
  4: '#FFFFFF',
  5: '#000000',
  7: '#000000',
  '-10': '#000000'
}

const EditableCell = ({
  item,
  customerCode,
  originalValue,
  distributionManager,
  onFocus,
  readOnly = false
}) => {
  const inputRef = useRef(null)
  const [localValue, setLocalValue] = useState('')
  const [backgroundColor, setBackgroundColor] = useState()
  const [textColor, setTextColor] = useState()
  const [isEdited, setIsEdited] = useState(false)

  const commitByEnterRef = useRef(false)
  const updateScheduledRef = useRef(false)
  const controlKeyRef = useRef({ pending: false, combined: false })

  const cellId = useMemo(
    () => ({
      hinmeiCode: String(item.hinmeiCode),
      niukabango: String(item.niukabango),
      customerCode: String(customerCode)
    }),
    [item.hinmeiCode, item.niukabango, customerCode]
  )

  const getDistEntry = useCallback(() => {
    // Branch renames, column deletes and saves only update the manager's rows;
    // the item prop keeps the allocations it was rendered with.
    const realItem = distributionManager._getRealItem(item)
    const dists = realItem.distribution_result || []

    if (!ColumnManager.isCompositeKey(customerCode)) {
      return (
        dists.find((d) => String(d.customer_code) === String(customerCode)) ??
        null
      )
    }

    const {
      customerCode: code,
      branchCode,
      index
    } = ColumnManager.parseColumnKey(customerCode)

    const matching = dists.filter(
      (d) =>
        String(d.customer_code) === code &&
        String(d.branch_code ?? '') === branchCode
    )

    return matching[index] ?? null
  }, [item, customerCode, distributionManager])

  const tooltipContent = useMemo(() => {
    const dist = getDistEntry()
    if (!dist) return ''

    if (dist.status === -10) {
      return (
        <div className='text-xs leading-7'>
          <div>
            <span className='font-bold text-red-400'>
              {dist.original_order_quantity ?? ''}
            </span>
          </div>
        </div>
      )
    }

    const hasAnyValue = [
      dist.original_sanchi,
      dist.original_grade,
      dist.original_size,
      dist.original_order_quantity,
      dist.original_scale
    ].some((v) => v !== null && v !== undefined && String(v).trim() !== '')

    if (!hasAnyValue) return ''

    return (
      <div className='text-xs leading-7'>
        <div>顧客のオーダー</div>
        <div>
          <span className='opacity-70'>産地: </span>
          <span className='font-bold'>{dist.original_sanchi ?? ''}</span>
        </div>
        <div>
          <span className='opacity-70'>等級: </span>
          <span className='font-bold'>{dist.original_grade ?? ''}</span>
        </div>
        <div>
          <span className='opacity-70'>サイズ: </span>
          <span className='font-bold'>{dist.original_size ?? ''}</span>
        </div>
        <div>
          <span className='opacity-70'>注文数: </span>
          <span className='font-bold'>
            {dist.original_order_quantity ?? ''}
          </span>
        </div>
        <div>
          <span className='opacity-70'>注文重量: </span>
          <span className='font-bold'>{dist.original_scale ?? ''}</span>
        </div>
      </div>
    )
  }, [getDistEntry])

  const checkIfEdited = useCallback(() => {
    const currentVal = distributionManager.getValue(
      item.hinmeiCode,
      item.niukabango,
      customerCode,
      originalValue
    )

    const currentQty = distributionManager.parseQuantity(currentVal)
    const dist = getDistEntry()
    const originalQty = distributionManager.parseQuantity(originalValue ?? 0)
    if (
      dist &&
      Object.prototype.hasOwnProperty.call(dist, 'original_delivery_quantity')
    ) {
      const savedOriginalQty = distributionManager.parseQuantity(
        dist.original_delivery_quantity
      )
      return currentQty > 0 && currentQty !== savedOriginalQty
    }

    if ((dist?.user_edited || Number(dist?.status) === 6) && currentQty > 0) {
      return true
    }

    return currentQty !== originalQty
  }, [
    item.hinmeiCode,
    item.niukabango,
    customerCode,
    distributionManager,
    originalValue,
    getDistEntry
  ])

  const updateStatusColor = useCallback(() => {
    if (
      distributionManager.isCellReset(
        item.hinmeiCode,
        item.niukabango,
        customerCode
      )
    ) {
      setBackgroundColor(STATUS_COLOR_MAP[0])
      setIsEdited(false)
      return
    }

    const currentVal = distributionManager.getValue(
      item.hinmeiCode,
      item.niukabango,
      customerCode,
      originalValue
    )

    if (currentVal === '' || currentVal === null || currentVal === undefined) {
      setBackgroundColor(STATUS_COLOR_MAP[0])
      setIsEdited(checkIfEdited())
      return
    }

    const numVal = distributionManager.parseQuantity(currentVal)
    if (numVal === 0) {
      setBackgroundColor(STATUS_COLOR_MAP[0])
      setIsEdited(checkIfEdited())
      return
    }

    const dist = getDistEntry()
    const status = dist?.status
    const currentQty = distributionManager.parseQuantity(currentVal)
    const originalQty = distributionManager.parseQuantity(originalValue ?? 0)
    const orderQty = distributionManager.parseQuantity(
      dist?.original_order_quantity ?? 0
    )
    

    if (status === 3) {
      if (currentQty < originalQty) {
        setBackgroundColor(STATUS_COLOR_MAP[2])
        setTextColor(STATUS_TEXT_COLOR_MAP[2])
      } else if (currentQty > originalQty) {
        setBackgroundColor(STATUS_COLOR_MAP[4])
        setTextColor(STATUS_TEXT_COLOR_MAP[4])
      } else {
        setBackgroundColor(STATUS_COLOR_MAP[3])
        setTextColor(STATUS_TEXT_COLOR_MAP[3])
      }
    } else if (status === 2) {
      // Mặc định màu 2, bằng orderQty → 3, lớn hơn → 4
      if (currentQty === orderQty) {
        setBackgroundColor(STATUS_COLOR_MAP[3])
        setTextColor(STATUS_TEXT_COLOR_MAP[3])
      } else if (currentQty > orderQty) {
        setBackgroundColor(STATUS_COLOR_MAP[4])
        setTextColor(STATUS_TEXT_COLOR_MAP[4])
      } else {
        setBackgroundColor(STATUS_COLOR_MAP[2])
        setTextColor(STATUS_TEXT_COLOR_MAP[2])
      }
    } else if (status === 4) {
      if (currentQty === orderQty) {
        setBackgroundColor(STATUS_COLOR_MAP[3])
        setTextColor(STATUS_TEXT_COLOR_MAP[3])
      } else if (currentQty < orderQty) {
        setBackgroundColor(STATUS_COLOR_MAP[2])
        setTextColor(STATUS_TEXT_COLOR_MAP[2])
      } else {
        setBackgroundColor(STATUS_COLOR_MAP[4])
        setTextColor(STATUS_TEXT_COLOR_MAP[4])
      }
    } else {
      const bg = STATUS_COLOR_MAP[status] ?? STATUS_COLOR_MAP[0]
      setBackgroundColor(bg)
      setTextColor(STATUS_TEXT_COLOR_MAP[status] ?? '#000000')
    }

    setIsEdited(checkIfEdited())
  }, [
    item.hinmeiCode,
    item.niukabango,
    customerCode,
    originalValue,
    distributionManager,
    checkIfEdited,
    getDistEntry
  ])

  useEffect(() => {
    const value = distributionManager.getValue(
      item.hinmeiCode,
      item.niukabango,
      customerCode,
      originalValue
    )
    setLocalValue(String(value) ?? '')
    updateStatusColor()
  }, [
    item.hinmeiCode,
    item.niukabango,
    customerCode,
    originalValue,
    distributionManager,
    updateStatusColor
  ])

  useEffect(() => {
    const handleCommitted = (data) => {
      const matches =
        String(data.hinmeiCode) === cellId.hinmeiCode &&
        String(data.niukabango) === cellId.niukabango &&
        String(data.customerCode) === cellId.customerCode

      if (!matches) return

      const newValue =
        data.value === '' || data.value === null || data.value === undefined
          ? ''
          : String(data.value)

      if (!updateScheduledRef.current) {
        updateScheduledRef.current = true
        requestAnimationFrame(() => {
          setLocalValue(newValue)
          if (inputRef.current) inputRef.current.value = newValue
          updateStatusColor()
          updateScheduledRef.current = false
        })
      } else {
        setLocalValue(newValue)
      }
    }

    return eventBus.on(EVENTS.DISTRIBUTION_COMMITTED, handleCommitted)
  }, [cellId, updateStatusColor])

  useEffect(() => {
    const handleSyncData = () => {
      const value = distributionManager.getValue(
        item.hinmeiCode,
        item.niukabango,
        customerCode,
        originalValue
      )
      const newValue =
        value === '' || value === null || value === undefined
          ? ''
          : String(value)

      requestAnimationFrame(() => {
        setLocalValue(newValue)
        if (inputRef.current) inputRef.current.value = newValue
        updateStatusColor()
      })
    }

    return eventBus.on(EVENTS.DISTRIBUTION_RESET, handleSyncData)
  }, [
    item.hinmeiCode,
    item.niukabango,
    customerCode,
    originalValue,
    distributionManager,
    updateStatusColor
  ])

  const handleChange = useCallback(
    (e) => {
      if (readOnly) return
      const value = e.target.value
      if (!/^\d*$/.test(value)) return
      setLocalValue(value)
      distributionManager.setDraftValue(
        item.hinmeiCode,
        item.niukabango,
        customerCode,
        value
      )
      updateStatusColor()
    },
    [
      item.hinmeiCode,
      item.niukabango,
      customerCode,
      distributionManager,
      updateStatusColor,
      readOnly
    ]
  )


  const ensureVisible = (element) => {
    const scrollContainer = element.closest('.rp-table-scroll')
    if (!scrollContainer) return
    
    const rect = element.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()
    
    const stickyCols = Array.from(scrollContainer.querySelectorAll('.sticky-col'))
    let stickyWidth = 0
    if (stickyCols.length > 0) {
      const lastSticky = stickyCols[stickyCols.length - 1]
      const lastStickyRect = lastSticky.getBoundingClientRect()
      stickyWidth = lastStickyRect.right - containerRect.left
    }

    if (rect.left < containerRect.left + stickyWidth) {
      scrollContainer.scrollLeft -= (containerRect.left + stickyWidth - rect.left + 20)
    } else if (rect.right > containerRect.right) {
      scrollContainer.scrollLeft += (rect.right - containerRect.right + 20)
    }

    const thead = scrollContainer.querySelector('thead')
    const headerHeight = thead ? thead.getBoundingClientRect().height : 0

    if (rect.top < containerRect.top + headerHeight) {
      scrollContainer.scrollTop -= (containerRect.top + headerHeight - rect.top + 20)
    } else if (rect.bottom > containerRect.bottom) {
      scrollContainer.scrollTop += (rect.bottom - containerRect.bottom + 20)
    }
  }

  const findNextInput = useCallback((currentInput, direction) => {
    const currentTd = currentInput.closest('td, th')
    if (!currentTd) return null
    const currentTr = currentTd.closest('tr')
    if (!currentTr) return null
    
    const allTrs = Array.from(document.querySelectorAll('.rp-table tr'))
    const currentTrIdx = allTrs.indexOf(currentTr)

    const isAutocomplete = (el) => el.closest('.MuiAutocomplete-root')

    if (direction === 'up' || direction === 'down') {
      const step = direction === 'up' ? -1 : 1
      const currentRect = currentTd.getBoundingClientRect()
      const currentCenter = currentRect.left + currentRect.width / 2

      for (let i = currentTrIdx + step; i >= 0 && i < allTrs.length; i += step) {
        const row = allTrs[i]
        const cells = Array.from(row.cells)
        
        const targetTd = cells.find(td => {
           const rect = td.getBoundingClientRect()
           return currentCenter >= rect.left && currentCenter <= rect.right
        })

        if (targetTd) {
          const input = targetTd.querySelector('input')
          if (input && !input.disabled && !input.readOnly && !isAutocomplete(input)) {
            return input
          }
        }
      }
    } else if (direction === 'left' || direction === 'right') {
      const allInputs = Array.from(
        document.querySelectorAll('.rp-table input')
      ).filter(i => !i.disabled && !i.readOnly && !isAutocomplete(i))
      
      const currentIndex = allInputs.indexOf(currentInput)
      if (direction === 'left' && currentIndex > 0) return allInputs[currentIndex - 1]
      if (direction === 'right' && currentIndex < allInputs.length - 1) return allInputs[currentIndex + 1]
    }
    return null
  }, [])

  const handleBlur = useCallback(
    (e) => {
      controlKeyRef.current = { pending: false, combined: false }
      if (readOnly) return
      if (commitByEnterRef.current) return
      const cellKey = distributionManager.getCellKey(
        item.hinmeiCode,
        item.niukabango,
        customerCode
      )
      if (!(cellKey in distributionManager.draftCells)) return
      const committed = distributionManager.commitChange(
        item.hinmeiCode,
        item.niukabango,
        customerCode,
        e.target.value
      )
      setLocalValue(committed === '' ? '' : String(committed))
    },
    [
      item.hinmeiCode,
      item.niukabango,
      customerCode,
      distributionManager,
      readOnly
    ]
  )

  const handleFocus = useCallback(
    (e) => {
      if (readOnly) return
      e.target.select()
      if (onFocus) onFocus()
      eventBus.emit(EVENTS.CELL_FOCUSED, {
        hinmeiCode: item.hinmeiCode,
        niukabango: item.niukabango,
        customerCode
      })
    },
    [item.hinmeiCode, item.niukabango, customerCode, onFocus, readOnly]
  )

  const handleKeyDown = useCallback(
    (e) => {
      if (readOnly) return
      const { key, currentTarget } = e
      if (key === 'Control') {
        controlKeyRef.current = { pending: true, combined: false }
        return
      }
      if (e.ctrlKey || controlKeyRef.current.pending) {
        if (controlKeyRef.current.pending) {
          controlKeyRef.current.combined = true
        }
        return
      }
      const keyboardDirection = getKeyboardNavigationDirection(key)
      if (keyboardDirection) {
        e.preventDefault()
        commitByEnterRef.current = true

        const value = currentTarget.value
        const committed = distributionManager.commitChange(
          item.hinmeiCode,
          item.niukabango,
          customerCode,
          value
        )
        setLocalValue(committed === '' ? '' : String(committed))

        const nextInput = findNextInput(currentTarget, keyboardDirection)
        if (nextInput) {
          nextInput.focus()
          nextInput.select()
          ensureVisible(nextInput)
        }
        setTimeout(() => {
          commitByEnterRef.current = false
        }, 0)
      } else if (key === 'Escape') {
        e.preventDefault()
        distributionManager.clearDraftValue(
          item.hinmeiCode,
          item.niukabango,
          customerCode
        )
        const committedValue = distributionManager.getValue(
          item.hinmeiCode,
          item.niukabango,
          customerCode,
          originalValue
        )
        const display = committedValue === '' ? '' : String(committedValue)
        setLocalValue(display)
        if (inputRef.current) inputRef.current.value = display
      } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
        // Handle arrow navigation
        const isLeft = key === 'ArrowLeft'
        const isRight = key === 'ArrowRight'
        const isUp = key === 'ArrowUp'
        const isDown = key === 'ArrowDown'

        if (isLeft && currentTarget.selectionStart !== 0) return
        if (isRight && currentTarget.selectionEnd !== currentTarget.value.length) return

        const direction = isUp ? 'up' : isDown ? 'down' : isLeft ? 'left' : 'right'
        const targetInput = findNextInput(currentTarget, direction)
        
        if (targetInput) {
          e.preventDefault()
          targetInput.focus()
          targetInput.select()
          ensureVisible(targetInput)
        }
      }
    },
    [
      item.hinmeiCode,
      item.niukabango,
      customerCode,
      originalValue,
      distributionManager,
      findNextInput,
    readOnly
    ]
  )

  const handleKeyUp = useCallback(
    (e) => {
      if (e.key !== 'Control') return
      const controlState = controlKeyRef.current
      controlKeyRef.current = { pending: false, combined: false }
      if (readOnly || !controlState.pending || controlState.combined) return

      e.preventDefault()
      const value = e.currentTarget.value
      const committed = distributionManager.commitChange(
        item.hinmeiCode,
        item.niukabango,
        customerCode,
        value
      )
      setLocalValue(committed === '' ? '' : String(committed))

      const nextInput = findNextInput(e.currentTarget, 'left')
      if (nextInput) {
        nextInput.focus()
        nextInput.select()
        ensureVisible(nextInput)
      }
    },
    [
      customerCode,
      distributionManager,
      findNextInput,
      item.hinmeiCode,
      item.niukabango,
      readOnly
    ]
  )

  return (
    <Tooltip
      title={tooltipContent}
      placement='top'
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(50,50,50,0.95)',
            '& .MuiTooltip-arrow': { color: 'rgba(50,50,50,0.95)' },
            borderRadius: 1,
            px: 1.5,
            py: 1
          }
        }
      }}
    >
      <input
        ref={inputRef}
        type='text'
        className='rp-cell-input'
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        onFocus={handleFocus}
        title=''
        placeholder=''
        readOnly={readOnly}
        style={{
          backgroundColor: backgroundColor,
          color: textColor,
          fontWeight: isEdited ? 'bold' : 'normal'
        }}
      />
    </Tooltip>
  )
}

export default React.memo(EditableCell, (prevProps, nextProps) => {
  return (
    prevProps.item.hinmeiCode === nextProps.item.hinmeiCode &&
    prevProps.item.niukabango === nextProps.item.niukabango &&
    prevProps.customerCode === nextProps.customerCode &&
    prevProps.originalValue === nextProps.originalValue &&
    prevProps.distributionManager === nextProps.distributionManager &&
    prevProps.readOnly === nextProps.readOnly
  )
})
